/**
 * Startbestand: Übungen und Pläne, die ein frisches Konto mitbekommt.
 *
 * **Das hier sind Vorlagen, kein Kanon.** Beim Anlegen des Kontos werden die
 * Zeilen in die Tabellen des Nutzers *kopiert* — danach gehören sie ihm: löschen,
 * umbenennen, Spanne ändern, Video eintragen. Ein leeres Konto wäre die
 * Alternative gewesen; dann müsste man vor der ersten Einheit vierzig Übungen
 * abtippen.
 *
 * **Ohne Videos.** Adressen zu Technikvideos stehen hier bewusst nicht: Erfundene
 * Verweise wären schlimmer als keine — sie sehen aus wie geprüfte Anleitungen.
 * Das Feld ist da, der Editor füllt es, und die Oberfläche sagt ausdrücklich
 * „kein Video hinterlegt", statt die Lücke zu verstecken.
 *
 * Die Hinweise (`cues`) sind Kurzmerker für jemanden, der die Bewegung kennt —
 * **kein Techniktraining**. Genau das steht auch in der App an der Übung.
 */

import type { BodyRegion, Equipment, MovementPattern, Muscle, TrackingMode } from './exercises.js';

export interface CatalogExercise {
  /** Stabiler Schlüssel — Wiedererkennung beim Nachladen, nie in der Oberfläche. */
  key: string;
  /** Zu welcher Familie die Variante gehört. Genau eine Ebene, nie tiefer. */
  parentKey?: string;
  name: string;
  nameEn: string;
  /** Kurzform für Listen und Diagrammachsen („Schrägbank, KH"). */
  variantLabel?: string;
  variantLabelEn?: string;
  trackingMode: TrackingMode;
  equipment: Equipment;
  primaryMuscles: readonly Muscle[];
  secondaryMuscles: readonly Muscle[];
  movementPattern: MovementPattern;
  bodyRegion: BodyRegion;
  isCompound: boolean;
  isUnilateral: boolean;
  defaultSets: number;
  repMin: number;
  repMax: number;
  /** Kleinste Stufe, in der sich diese Übung überhaupt steigern lässt. */
  incrementKg: number;
  restSeconds: number;
  /** Leer bei Varianten, die technisch dasselbe sind wie ihr Grundtyp. */
  cues?: readonly string[];
  cuesEn?: readonly string[];
}

/**
 * Pausenzeiten nach Fachkonzept §1: Grundübung schwer und wenige Sätze, deshalb
 * lange Pause; Zusatzübungen kurz.
 */
const REST_COMPOUND = 180;
const REST_ACCESSORY = 90;
const REST_ISOLATION = 60;

export const STARTER_EXERCISES: readonly CatalogExercise[] = [
  // --- Unterkörper: Kniebeugemuster ------------------------------------------
  {
    key: 'squat',
    name: 'Kniebeuge',
    nameEn: 'Squat',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['hamstrings', 'lowerBack', 'core'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Ganze Fußsohle belastet, Mittelfuß über der Stange.',
      'Brust hoch, Rumpf fest — Luft anhalten bis über den schwersten Punkt.',
      'Knie folgen der Fußspitze, nicht nach innen fallen lassen.',
    ],
    cuesEn: [
      'Whole foot loaded, bar over midfoot.',
      'Chest up, brace hard — hold the breath past the sticking point.',
      'Knees track over the toes, never collapse inward.',
    ],
  },
  {
    key: 'squat-front',
    parentKey: 'squat',
    name: 'Frontkniebeuge',
    nameEn: 'Front Squat',
    variantLabel: 'Front, LH',
    variantLabelEn: 'Front, BB',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['quads'],
    secondaryMuscles: ['glutes', 'core', 'lowerBack'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: ['Ellbogen hoch — fällt der Ellbogen, fällt die Stange.'],
    cuesEn: ['Elbows high — a dropping elbow drops the bar.'],
  },
  {
    key: 'squat-bodyweight',
    parentKey: 'squat',
    name: 'Kniebeuge ohne Gewicht',
    nameEn: 'Bodyweight Squat',
    variantLabel: 'Körpergewicht',
    variantLabelEn: 'bodyweight',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['hamstrings', 'core'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 0,
    restSeconds: REST_ACCESSORY,
    cues: [
      'Bei 3×8 sauber: weiter zum Ausfallschritt, dann zum Pistol-Squat.',
      'Knie folgen der Fußspitze, Fersen bleiben am Boden.',
    ],
    cuesEn: [
      'Clean 3×8: move on to the split squat, then the pistol squat.',
      'Knees track over the toes, heels stay down.',
    ],
  },
  {
    key: 'squat-goblet',
    parentKey: 'squat',
    name: 'Goblet-Kniebeuge',
    nameEn: 'Goblet Squat',
    variantLabel: 'Goblet, KH',
    variantLabelEn: 'Goblet, DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['core'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'legpress',
    name: 'Beinpresse',
    nameEn: 'Leg Press',
    trackingMode: 'weightReps',
    equipment: 'machine',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['hamstrings'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 5,
    restSeconds: REST_ACCESSORY,
    cues: ['Unterer Rücken bleibt an der Lehne — nicht so tief, dass das Becken kippt.'],
    cuesEn: ['Lower back stays on the pad — not so deep that the pelvis tucks.'],
  },
  {
    key: 'splitsquat',
    name: 'Bulgarischer Ausfallschritt',
    nameEn: 'Bulgarian Split Squat',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['hamstrings', 'core'],
    movementPattern: 'lunge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
    cues: ['Vorderer Fuß weit genug vorn, damit das Knie über dem Mittelfuß bleibt.'],
    cuesEn: ['Front foot far enough forward that the knee stays over the midfoot.'],
  },
  {
    key: 'lunge',
    name: 'Ausfallschritt',
    nameEn: 'Lunge',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['hamstrings', 'core'],
    movementPattern: 'lunge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'pistolsquat',
    name: 'Pistol-Squat (Progression)',
    nameEn: 'Pistol Squat (progression)',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['quads', 'glutes'],
    secondaryMuscles: ['core', 'hamstrings'],
    movementPattern: 'squat',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 3,
    repMax: 8,
    incrementKg: 0,
    restSeconds: REST_ACCESSORY,
    cues: [
      'Zuerst auf eine Bank, dann tiefer — die Progression ist die Übung.',
      'Fersenkontakt halten; hebt die Ferse, ist die Kiste noch zu tief.',
    ],
    cuesEn: [
      'Start to a bench, then lower — the progression is the exercise.',
      'Keep the heel down; a lifting heel means the box is still too low.',
    ],
  },

  // --- Unterkörper: Hüftstreckung --------------------------------------------
  {
    key: 'deadlift',
    name: 'Kreuzheben',
    nameEn: 'Deadlift',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['hamstrings', 'glutes', 'lowerBack'],
    secondaryMuscles: ['back', 'forearms', 'core'],
    movementPattern: 'hinge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 3,
    repMax: 6,
    incrementKg: 5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Stange am Schienbein, Latissimus spannen — die Stange bleibt am Körper.',
      'Hüfte und Schultern gehen gleichzeitig hoch.',
    ],
    cuesEn: [
      'Bar against the shin, lats engaged — the bar stays on the body.',
      'Hips and shoulders rise at the same time.',
    ],
  },
  {
    key: 'rdl',
    parentKey: 'deadlift',
    name: 'Rumänisches Kreuzheben',
    nameEn: 'Romanian Deadlift',
    variantLabel: 'RDL, LH',
    variantLabelEn: 'RDL, BB',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['hamstrings', 'glutes'],
    secondaryMuscles: ['lowerBack', 'forearms'],
    movementPattern: 'hinge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: ['Hüfte nach hinten schieben, Knie fast gestreckt — Dehnung in der Rückseite.'],
    cuesEn: ['Push the hips back, knees nearly straight — stretch in the hamstrings.'],
  },
  {
    key: 'rdl-singleleg',
    parentKey: 'deadlift',
    name: 'Einbeiniges Kreuzheben',
    nameEn: 'Single-Leg Romanian Deadlift',
    variantLabel: 'einbeinig, KG',
    variantLabelEn: 'single-leg, BW',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['hamstrings', 'glutes'],
    secondaryMuscles: ['lowerBack', 'core'],
    movementPattern: 'hinge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 0,
    restSeconds: REST_ACCESSORY,
    cues: [
      'Zu Beginn beidbeinig ohne Gewicht; bei 3×8 auf ein Bein.',
      'Hüfte bleibt waagerecht — nicht zur Seite aufdrehen.',
    ],
    cuesEn: [
      'Start on both legs without weight; at 3×8 move to one leg.',
      'Hips stay square — don’t open to the side.',
    ],
  },
  {
    key: 'hipthrust',
    name: 'Hüftstoß',
    nameEn: 'Hip Thrust',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['glutes'],
    secondaryMuscles: ['hamstrings', 'core'],
    movementPattern: 'hinge',
    bodyRegion: 'lower',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 5,
    restSeconds: REST_ACCESSORY,
    cues: ['Oben Rippen unten lassen — die Bewegung endet in der Hüfte, nicht im Rücken.'],
    cuesEn: ['Keep the ribs down at the top — the movement ends in the hips, not the back.'],
  },
  {
    key: 'legcurl',
    name: 'Beinbeuger',
    nameEn: 'Leg Curl',
    trackingMode: 'weightReps',
    equipment: 'machine',
    primaryMuscles: ['hamstrings'],
    secondaryMuscles: ['calves'],
    movementPattern: 'isolation',
    bodyRegion: 'lower',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 5,
    restSeconds: REST_ISOLATION,
  },
  {
    key: 'legextension',
    name: 'Beinstrecker',
    nameEn: 'Leg Extension',
    trackingMode: 'weightReps',
    equipment: 'machine',
    primaryMuscles: ['quads'],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'lower',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 5,
    restSeconds: REST_ISOLATION,
  },
  {
    key: 'calfraise',
    name: 'Wadenheben',
    nameEn: 'Calf Raise',
    trackingMode: 'weightReps',
    equipment: 'machine',
    primaryMuscles: ['calves'],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'lower',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 5,
    restSeconds: REST_ISOLATION,
    cues: ['Unten kurz in die Dehnung, oben eine Sekunde halten.'],
    cuesEn: ['Brief stretch at the bottom, hold a second at the top.'],
  },

  // --- Oberkörper: Drücken ----------------------------------------------------
  {
    key: 'bench',
    name: 'Bankdrücken',
    nameEn: 'Bench Press',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['chest'],
    secondaryMuscles: ['triceps', 'shoulders'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Schulterblätter zusammen und fixiert, Füße fest am Boden.',
      'Ellbogen etwa 45° zum Körper, nicht rechtwinklig abspreizen.',
      'Ohne Ablage nur mit Sicherung oder im Rack.',
    ],
    cuesEn: [
      'Shoulder blades pinched and fixed, feet planted.',
      'Elbows about 45° to the body, never flared straight out.',
      'Only with a spotter or in the rack.',
    ],
  },
  {
    key: 'bench-incline',
    parentKey: 'bench',
    name: 'Schrägbankdrücken',
    nameEn: 'Incline Bench Press',
    variantLabel: 'Schrägbank, LH',
    variantLabelEn: 'Incline, BB',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['chest', 'shoulders'],
    secondaryMuscles: ['triceps'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: ['30–45° reichen; steiler wird es Schulterdrücken.'],
    cuesEn: ['30–45° is enough; steeper turns it into an overhead press.'],
  },
  {
    key: 'bench-db',
    parentKey: 'bench',
    name: 'Bankdrücken mit Kurzhanteln',
    nameEn: 'Dumbbell Bench Press',
    variantLabel: 'Flach, KH',
    variantLabelEn: 'Flat, DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['chest'],
    secondaryMuscles: ['triceps', 'shoulders'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'bench-incline-db',
    parentKey: 'bench',
    name: 'Schrägbankdrücken mit Kurzhanteln',
    nameEn: 'Incline Dumbbell Press',
    variantLabel: 'Schrägbank, KH',
    variantLabelEn: 'Incline, DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['chest', 'shoulders'],
    secondaryMuscles: ['triceps'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'pushup',
    name: 'Liegestütz',
    nameEn: 'Push-up',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['chest'],
    secondaryMuscles: ['triceps', 'shoulders', 'core'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 20,
    incrementKg: 0,
    restSeconds: REST_ACCESSORY,
    cues: ['Körper bleibt eine Linie — Hüfte hängt nicht durch.'],
    cuesEn: ['Body stays one line — no sagging hips.'],
  },
  {
    key: 'dips',
    name: 'Dips',
    nameEn: 'Dips',
    trackingMode: 'weightedBodyweight',
    equipment: 'bodyweight',
    primaryMuscles: ['chest', 'triceps'],
    secondaryMuscles: ['shoulders'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 12,
    incrementKg: 2.5,
    restSeconds: REST_ACCESSORY,
    cues: [
      'Nur so tief, wie die Schulter es schmerzfrei zulässt.',
      'Leicht vorgelehnt trifft es die Brust, aufrecht den Trizeps.',
    ],
    cuesEn: [
      'Only as deep as the shoulder allows without pain.',
      'Leaning forward hits the chest, upright hits the triceps.',
    ],
  },
  {
    key: 'ohp',
    name: 'Schulterdrücken',
    nameEn: 'Overhead Press',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['shoulders'],
    secondaryMuscles: ['triceps', 'core'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Gesäß und Bauch fest — sonst wird der untere Rücken zur Bank.',
      'Kopf leicht zurück, Stange dicht am Gesicht vorbei.',
    ],
    cuesEn: [
      'Glutes and abs tight — otherwise the lower back becomes the bench.',
      'Head back slightly, bar passes close to the face.',
    ],
  },
  {
    key: 'ohp-db',
    parentKey: 'ohp',
    name: 'Schulterdrücken mit Kurzhanteln',
    nameEn: 'Dumbbell Shoulder Press',
    variantLabel: 'KH',
    variantLabelEn: 'DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['shoulders'],
    secondaryMuscles: ['triceps'],
    movementPattern: 'push',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'lateralraise',
    name: 'Seitheben',
    nameEn: 'Lateral Raise',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['shoulders'],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 12,
    repMax: 15,
    incrementKg: 1,
    restSeconds: REST_ISOLATION,
    cues: ['Leicht angewinkelte Arme, bis Schulterhöhe — kein Schwung aus der Hüfte.'],
    cuesEn: ['Slight elbow bend, up to shoulder height — no hip swing.'],
  },
  {
    key: 'chestfly',
    name: 'Fliegende am Kabel',
    nameEn: 'Cable Fly',
    trackingMode: 'weightReps',
    equipment: 'cable',
    primaryMuscles: ['chest'],
    secondaryMuscles: ['shoulders'],
    movementPattern: 'isolation',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 2.5,
    restSeconds: REST_ISOLATION,
  },
  {
    key: 'triceps-pushdown',
    name: 'Trizepsdrücken am Kabel',
    nameEn: 'Triceps Pushdown',
    trackingMode: 'weightReps',
    equipment: 'cable',
    primaryMuscles: ['triceps'],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 2.5,
    restSeconds: REST_ISOLATION,
    cues: ['Ellbogen bleiben am Körper — nur der Unterarm bewegt sich.'],
    cuesEn: ['Elbows stay at the sides — only the forearm moves.'],
  },

  // --- Oberkörper: Ziehen -----------------------------------------------------
  {
    key: 'pullup',
    name: 'Klimmzug',
    nameEn: 'Pull-up',
    trackingMode: 'weightedBodyweight',
    equipment: 'bodyweight',
    primaryMuscles: ['lats', 'back'],
    secondaryMuscles: ['biceps', 'forearms', 'core'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 4,
    repMax: 10,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Aus dem toten Hang, Schulterblätter zuerst nach unten ziehen.',
      'Geht es noch nicht: negative Wiederholungen oder Band — beides zählt hier.',
    ],
    cuesEn: [
      'From a dead hang, pull the shoulder blades down first.',
      'Not there yet: negatives or a band — both count here.',
    ],
  },
  {
    key: 'pullup-chin',
    parentKey: 'pullup',
    name: 'Klimmzug im Untergriff',
    nameEn: 'Chin-up',
    variantLabel: 'Untergriff',
    variantLabelEn: 'Supinated',
    trackingMode: 'weightedBodyweight',
    equipment: 'bodyweight',
    primaryMuscles: ['lats', 'biceps'],
    secondaryMuscles: ['back', 'forearms'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 4,
    repMax: 10,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
  },
  {
    key: 'latpulldown',
    name: 'Latzug',
    nameEn: 'Lat Pulldown',
    trackingMode: 'weightReps',
    equipment: 'cable',
    primaryMuscles: ['lats'],
    secondaryMuscles: ['back', 'biceps'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 5,
    restSeconds: REST_ACCESSORY,
    cues: ['Zur Brust ziehen, nicht in den Nacken.'],
    cuesEn: ['Pull to the chest, never behind the neck.'],
  },
  {
    key: 'row-barbell',
    name: 'Langhantelrudern',
    nameEn: 'Barbell Row',
    trackingMode: 'weightReps',
    equipment: 'barbell',
    primaryMuscles: ['back', 'lats'],
    secondaryMuscles: ['biceps', 'rearDelts', 'lowerBack'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    incrementKg: 2.5,
    restSeconds: REST_COMPOUND,
    cues: [
      'Oberkörper bleibt ruhig — kein Aufrichten, um mehr zu schaffen.',
      'Zum unteren Brustkorb ziehen, kurz halten.',
    ],
    cuesEn: [
      'Torso stays still — no heaving upright to get the rep.',
      'Pull to the lower ribs, brief pause.',
    ],
  },
  {
    key: 'row-db',
    parentKey: 'row-barbell',
    name: 'Kurzhantelrudern einarmig',
    nameEn: 'One-Arm Dumbbell Row',
    variantLabel: 'einarmig, KH',
    variantLabelEn: 'one-arm, DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['back', 'lats'],
    secondaryMuscles: ['biceps', 'rearDelts'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'row-inverted',
    parentKey: 'row-barbell',
    name: 'Rudern am Tisch oder an Ringen',
    nameEn: 'Inverted Row',
    variantLabel: 'Körpergewicht',
    variantLabelEn: 'bodyweight',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['back', 'lats'],
    secondaryMuscles: ['biceps', 'rearDelts', 'core'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 5,
    repMax: 8,
    incrementKg: 0,
    restSeconds: REST_ACCESSORY,
    cues: [
      'Je aufrechter der Körper, desto leichter — so beginnt die Progression.',
      'Körper bleibt eine Linie, Brust zur Stange.',
    ],
    cuesEn: [
      'The more upright the body, the easier — that is where the progression starts.',
      'Body stays one line, chest to the bar.',
    ],
  },
  {
    key: 'row-cable',
    parentKey: 'row-barbell',
    name: 'Rudern am Kabel sitzend',
    nameEn: 'Seated Cable Row',
    variantLabel: 'Kabel, sitzend',
    variantLabelEn: 'cable, seated',
    trackingMode: 'weightReps',
    equipment: 'cable',
    primaryMuscles: ['back'],
    secondaryMuscles: ['lats', 'biceps', 'rearDelts'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 5,
    restSeconds: REST_ACCESSORY,
  },
  {
    key: 'facepull',
    name: 'Face Pull',
    nameEn: 'Face Pull',
    trackingMode: 'weightReps',
    equipment: 'cable',
    primaryMuscles: ['rearDelts'],
    secondaryMuscles: ['back', 'shoulders'],
    movementPattern: 'pull',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 12,
    repMax: 20,
    incrementKg: 2.5,
    restSeconds: REST_ISOLATION,
    cues: ['Zur Stirn ziehen, Ellbogen hoch — das Gegengewicht zu allem Drücken.'],
    cuesEn: ['Pull to the forehead, elbows high — the counterweight to all pressing.'],
  },
  {
    key: 'curl',
    name: 'Bizepscurl',
    nameEn: 'Biceps Curl',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['biceps'],
    secondaryMuscles: ['forearms'],
    movementPattern: 'isolation',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 2,
    restSeconds: REST_ISOLATION,
  },
  {
    key: 'curl-hammer',
    parentKey: 'curl',
    name: 'Hammercurl',
    nameEn: 'Hammer Curl',
    variantLabel: 'Hammer, KH',
    variantLabelEn: 'hammer, DB',
    trackingMode: 'weightReps',
    equipment: 'dumbbell',
    primaryMuscles: ['biceps', 'forearms'],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'upper',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 2,
    restSeconds: REST_ISOLATION,
  },

  // --- Rumpf ------------------------------------------------------------------
  {
    key: 'plank',
    name: 'Unterarmstütz',
    nameEn: 'Plank',
    trackingMode: 'timeHold',
    equipment: 'bodyweight',
    primaryMuscles: ['core'],
    secondaryMuscles: ['shoulders', 'glutes'],
    movementPattern: 'core',
    bodyRegion: 'core',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 0,
    repMax: 0,
    incrementKg: 2.5,
    restSeconds: REST_ISOLATION,
    cues: ['Becken leicht einrollen, Gesäß anspannen — nicht durchhängen.'],
    cuesEn: ['Slight posterior tilt, squeeze the glutes — no sagging.'],
  },
  {
    key: 'hanging-legraise',
    name: 'Hängendes Beinheben',
    nameEn: 'Hanging Leg Raise',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['core'],
    secondaryMuscles: ['forearms'],
    movementPattern: 'core',
    bodyRegion: 'core',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 15,
    incrementKg: 0,
    restSeconds: REST_ISOLATION,
    cues: ['Ohne Schwung — lieber angewinkelte Knie als geschaukelte Beine.'],
    cuesEn: ['No swinging — bent knees beat rocking legs.'],
  },
  {
    key: 'abwheel',
    name: 'Bauchroller',
    nameEn: 'Ab Wheel',
    trackingMode: 'bodyweightReps',
    equipment: 'other',
    primaryMuscles: ['core'],
    secondaryMuscles: ['lats', 'shoulders'],
    movementPattern: 'core',
    bodyRegion: 'core',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 12,
    incrementKg: 0,
    restSeconds: REST_ISOLATION,
    cues: ['Nur so weit rollen, wie der untere Rücken flach bleibt.'],
    cuesEn: ['Roll only as far as the lower back stays flat.'],
  },
  {
    key: 'pallof',
    name: 'Pallof-Press',
    nameEn: 'Pallof Press',
    trackingMode: 'bodyweightReps',
    equipment: 'band',
    primaryMuscles: ['core'],
    secondaryMuscles: ['shoulders'],
    movementPattern: 'core',
    bodyRegion: 'core',
    isCompound: false,
    isUnilateral: true,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 0,
    restSeconds: REST_ISOLATION,
    cues: ['Seitlich zum Band, Arme gerade nach vorn — der Rumpf dreht nicht mit.'],
    cuesEn: ['Side-on to the band, press straight out — the torso does not rotate.'],
  },
  {
    key: 'backextension',
    name: 'Rückenstrecker',
    nameEn: 'Back Extension',
    trackingMode: 'bodyweightReps',
    equipment: 'bodyweight',
    primaryMuscles: ['lowerBack'],
    secondaryMuscles: ['glutes', 'hamstrings'],
    movementPattern: 'hinge',
    bodyRegion: 'core',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    incrementKg: 2.5,
    restSeconds: REST_ISOLATION,
  },
  {
    key: 'farmerwalk',
    name: "Farmer's Walk",
    nameEn: "Farmer's Walk",
    trackingMode: 'timeHold',
    equipment: 'dumbbell',
    primaryMuscles: ['forearms', 'core'],
    secondaryMuscles: ['back', 'shoulders'],
    movementPattern: 'carry',
    bodyRegion: 'fullBody',
    isCompound: true,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 0,
    repMax: 0,
    incrementKg: 2,
    restSeconds: REST_ACCESSORY,
    cues: ['Aufrecht gehen, Schultern hinten — die Zeit läuft, nicht die Strecke.'],
    cuesEn: ['Walk tall, shoulders back — time is the measure here, not distance.'],
  },

  // --- Ausdauer ---------------------------------------------------------------
  {
    key: 'run-easy',
    name: 'Lockerer Lauf',
    nameEn: 'Easy Run',
    trackingMode: 'distanceTime',
    equipment: 'other',
    primaryMuscles: ['cardio'],
    secondaryMuscles: ['quads', 'calves'],
    movementPattern: 'cardio',
    bodyRegion: 'fullBody',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 1,
    repMin: 0,
    repMax: 0,
    incrementKg: 0,
    restSeconds: 0,
    cues: ['Locker heißt: Unterhaltung möglich. Zu schnell gelaufen kostet das Krafttraining.'],
    cuesEn: ['Easy means: able to hold a conversation. Too fast here costs the lifting.'],
  },
  {
    key: 'run-intervals',
    parentKey: 'run-easy',
    name: 'Intervall-Lauf',
    nameEn: 'Interval Run',
    variantLabel: 'Intervalle',
    variantLabelEn: 'intervals',
    trackingMode: 'distanceTime',
    equipment: 'other',
    primaryMuscles: ['cardio'],
    secondaryMuscles: ['quads', 'calves'],
    movementPattern: 'cardio',
    bodyRegion: 'fullBody',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 1,
    repMin: 0,
    repMax: 0,
    incrementKg: 0,
    restSeconds: 0,
  },
];

// --- Pläne -------------------------------------------------------------------

export interface CatalogPlanExercise {
  exerciseKey: string;
  sets: number;
  repMin: number;
  repMax: number;
  /** Sekunden; fehlt sie, gilt die Vorgabe der Übung. */
  restSeconds?: number;
  /** Supersatz mit der Übung davor (`plan_exercises.superset_with_previous`). */
  supersetWithPrevious?: boolean;
}

export interface CatalogPlanDay {
  key: string;
  name: string;
  nameEn: string;
  exercises: readonly CatalogPlanExercise[];
}

export interface CatalogPlan {
  key: string;
  name: string;
  nameEn: string;
  note: string;
  noteEn: string;
  days: readonly CatalogPlanDay[];
}

/**
 * Die drei Pläne aus dem Fachkonzept §1 („12-Wochen-Aufbau") und Calisthenics 3×.
 *
 * Sie sind Startpunkte für den Editor, keine Vorschrift: Ganzkörper 3× für den
 * Wiedereinstieg, Hybrid für den Aufbau, Upper/Lower für die Progression.
 * Calisthenics 3× folgt der „Recommended Routine“ von r/bodyweightfitness
 * (Quelle im Fachkonzept §1): drei Paare als Supersatz, danach ein Rumpf-Dreier.
 */
export const STARTER_PLANS: readonly CatalogPlan[] = [
  {
    key: 'fullbody-3',
    name: 'Ganzkörper 3× (Wiedereinstieg)',
    nameEn: 'Full Body 3× (return to training)',
    note: 'Woche 1–2: Frequenz vor Volumen, etwa 60 % Gefühl.',
    noteEn: 'Weeks 1–2: frequency over volume, about 60 % effort.',
    days: [
      {
        key: 'fullbody-a',
        name: 'Ganzkörper A',
        nameEn: 'Full Body A',
        exercises: [
          { exerciseKey: 'squat', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'bench', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'row-barbell', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'ohp-db', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'legcurl', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'plank', sets: 3, repMin: 0, repMax: 0 },
        ],
      },
      {
        key: 'fullbody-b',
        name: 'Ganzkörper B',
        nameEn: 'Full Body B',
        exercises: [
          { exerciseKey: 'rdl', sets: 3, repMin: 6, repMax: 10 },
          { exerciseKey: 'pullup', sets: 3, repMin: 4, repMax: 10 },
          { exerciseKey: 'bench-incline-db', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'legpress', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'lateralraise', sets: 3, repMin: 12, repMax: 15 },
          { exerciseKey: 'hanging-legraise', sets: 3, repMin: 8, repMax: 15 },
        ],
      },
      {
        key: 'fullbody-c',
        name: 'Ganzkörper C',
        nameEn: 'Full Body C',
        exercises: [
          { exerciseKey: 'squat-front', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'dips', sets: 3, repMin: 6, repMax: 12 },
          { exerciseKey: 'latpulldown', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'splitsquat', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'curl', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'abwheel', sets: 3, repMin: 6, repMax: 12 },
        ],
      },
    ],
  },
  {
    key: 'hybrid-3-2',
    name: 'Hybrid: 3× Kraft + 2× Laufen',
    nameEn: 'Hybrid: 3× strength + 2× running',
    note: 'Woche 3–8: jede Muskelgruppe 3×/Woche, Läufe locker (Zone 2).',
    noteEn: 'Weeks 3–8: every muscle group 3×/week, easy runs (zone 2).',
    days: [
      {
        key: 'hybrid-kraft-a',
        name: 'Kraft A',
        nameEn: 'Strength A',
        exercises: [
          { exerciseKey: 'squat', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'bench', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'row-db', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'facepull', sets: 3, repMin: 12, repMax: 20 },
          { exerciseKey: 'plank', sets: 3, repMin: 0, repMax: 0 },
        ],
      },
      {
        key: 'hybrid-lauf-1',
        name: 'Lauf locker',
        nameEn: 'Easy run',
        exercises: [{ exerciseKey: 'run-easy', sets: 1, repMin: 0, repMax: 0 }],
      },
      {
        key: 'hybrid-kraft-b',
        name: 'Kraft B',
        nameEn: 'Strength B',
        exercises: [
          { exerciseKey: 'rdl', sets: 3, repMin: 6, repMax: 10 },
          { exerciseKey: 'ohp', sets: 3, repMin: 5, repMax: 8 },
          { exerciseKey: 'pullup', sets: 3, repMin: 4, repMax: 10 },
          { exerciseKey: 'legextension', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'hanging-legraise', sets: 3, repMin: 8, repMax: 15 },
        ],
      },
      {
        key: 'hybrid-lauf-2',
        name: 'Lauf locker',
        nameEn: 'Easy run',
        exercises: [{ exerciseKey: 'run-easy', sets: 1, repMin: 0, repMax: 0 }],
      },
      {
        key: 'hybrid-kraft-c',
        name: 'Kraft C',
        nameEn: 'Strength C',
        exercises: [
          { exerciseKey: 'legpress', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'bench-incline', sets: 3, repMin: 6, repMax: 10 },
          { exerciseKey: 'latpulldown', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'curl-hammer', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'calfraise', sets: 3, repMin: 10, repMax: 15 },
        ],
      },
    ],
  },
  {
    key: 'upper-lower-4',
    name: 'Upper/Lower 4×',
    nameEn: 'Upper/Lower 4×',
    note: 'Woche 9–12: mehr Volumen pro Muskelgruppe, wenn die Routine steht.',
    noteEn: 'Weeks 9–12: more volume per muscle group once the routine holds.',
    days: [
      {
        key: 'ul-ober-a',
        name: 'Oberkörper A',
        nameEn: 'Upper A',
        exercises: [
          { exerciseKey: 'bench', sets: 4, repMin: 5, repMax: 8 },
          { exerciseKey: 'row-barbell', sets: 4, repMin: 6, repMax: 10 },
          { exerciseKey: 'ohp-db', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'latpulldown', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'triceps-pushdown', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'curl', sets: 3, repMin: 10, repMax: 15 },
        ],
      },
      {
        key: 'ul-unter-a',
        name: 'Unterkörper A',
        nameEn: 'Lower A',
        exercises: [
          { exerciseKey: 'squat', sets: 4, repMin: 5, repMax: 8 },
          { exerciseKey: 'rdl', sets: 3, repMin: 6, repMax: 10 },
          { exerciseKey: 'legpress', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'legcurl', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'calfraise', sets: 4, repMin: 10, repMax: 15 },
        ],
      },
      {
        key: 'ul-ober-b',
        name: 'Oberkörper B',
        nameEn: 'Upper B',
        exercises: [
          { exerciseKey: 'ohp', sets: 4, repMin: 5, repMax: 8 },
          { exerciseKey: 'pullup', sets: 4, repMin: 4, repMax: 10 },
          { exerciseKey: 'bench-incline-db', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'row-cable', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'lateralraise', sets: 3, repMin: 12, repMax: 15 },
          { exerciseKey: 'facepull', sets: 3, repMin: 12, repMax: 20 },
        ],
      },
      {
        key: 'ul-unter-b',
        name: 'Unterkörper B',
        nameEn: 'Lower B',
        exercises: [
          { exerciseKey: 'deadlift', sets: 3, repMin: 3, repMax: 6 },
          { exerciseKey: 'splitsquat', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'hipthrust', sets: 3, repMin: 8, repMax: 12 },
          { exerciseKey: 'legextension', sets: 3, repMin: 10, repMax: 15 },
          { exerciseKey: 'backextension', sets: 3, repMin: 10, repMax: 15 },
        ],
      },
    ],
  },
  {
    key: 'calisthenics-3',
    name: 'Calisthenics 3×',
    nameEn: 'Calisthenics 3×',
    note: 'Nach der Recommended Routine (r/bodyweightfitness): 3× pro Woche dieselbe Einheit, Paare als Supersatz. Bei 3×8 die nächste Stufe der Übung.',
    noteEn:
      'After the Recommended Routine (r/bodyweightfitness): the same session 3× a week, pairs as supersets. At 3×8, the next step of the exercise.',
    days: [
      {
        key: 'calisthenics-a',
        name: 'Calisthenics',
        nameEn: 'Calisthenics',
        exercises: [
          { exerciseKey: 'pullup', sets: 3, repMin: 5, repMax: 8, restSeconds: 90 },
          {
            exerciseKey: 'squat-bodyweight',
            sets: 3,
            repMin: 5,
            repMax: 8,
            restSeconds: 90,
            supersetWithPrevious: true,
          },
          { exerciseKey: 'dips', sets: 3, repMin: 5, repMax: 8, restSeconds: 90 },
          {
            exerciseKey: 'rdl-singleleg',
            sets: 3,
            repMin: 5,
            repMax: 8,
            restSeconds: 90,
            supersetWithPrevious: true,
          },
          { exerciseKey: 'row-inverted', sets: 3, repMin: 5, repMax: 8, restSeconds: 90 },
          {
            exerciseKey: 'pushup',
            sets: 3,
            repMin: 5,
            repMax: 8,
            restSeconds: 90,
            supersetWithPrevious: true,
          },
          { exerciseKey: 'plank', sets: 3, repMin: 0, repMax: 0, restSeconds: 60 },
          {
            exerciseKey: 'pallof',
            sets: 3,
            repMin: 8,
            repMax: 12,
            restSeconds: 60,
            supersetWithPrevious: true,
          },
          {
            exerciseKey: 'backextension',
            sets: 3,
            repMin: 8,
            repMax: 12,
            restSeconds: 60,
            supersetWithPrevious: true,
          },
        ],
      },
    ],
  },
];

const BY_KEY = new Map(STARTER_EXERCISES.map((entry) => [entry.key, entry]));

export function catalogExercise(key: string): CatalogExercise | undefined {
  return BY_KEY.get(key);
}
